import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplicationStudioArtifactKind,
  ApplicationStudioCreateProjectInput,
  ApplicationStudioGenerateInput,
  ApplicationStudioSubmitApplyInput,
  ApplicationStudioApplyInput,
} from '@tabbio/types';
import {
  createApplicationStudioProject,
  deleteApplicationStudioArtifact,
  fetchApplicationStudioArtifact,
  fetchApplicationStudioArtifacts,
  fetchApplicationStudioProject,
  fetchApplicationStudioProjects,
  generateApplicationStudioArtifact,
  prepareApplicationStudioApply,
  regenerateApplicationStudioArtifact,
  submitApplicationStudioApply,
  updateApplicationStudioArtifact,
} from '../lib/application-studio-api';
import { QUERY_KEYS } from '../lib/query-keys';

export function useApplicationStudioProjects(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECTS,
    queryFn: fetchApplicationStudioProjects,
    enabled,
    staleTime: 30_000,
  });
}

export function useApplicationStudioProject(projectId?: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECT(projectId),
    queryFn: () => fetchApplicationStudioProject(projectId!),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });
}

export function useApplicationStudioArtifacts(input: {
  projectId?: string | null;
  kind?: ApplicationStudioArtifactKind;
  limit?: number;
} = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.APPLICATION_STUDIO_ARTIFACTS(
      input.projectId,
      input.kind,
      input.limit,
    ),
    queryFn: () =>
      fetchApplicationStudioArtifacts({
        projectId: input.projectId ?? undefined,
        kind: input.kind,
        limit: input.limit,
      }),
    staleTime: 20_000,
  });
}

export function useApplicationStudioArtifact(artifactId?: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.APPLICATION_STUDIO_ARTIFACT(artifactId),
    queryFn: () => fetchApplicationStudioArtifact(artifactId!),
    enabled: Boolean(artifactId),
    staleTime: 20_000,
  });
}

export function useCreateApplicationStudioProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplicationStudioCreateProjectInput) =>
      createApplicationStudioProject(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECTS,
      });
    },
  });
}

export function useGenerateApplicationStudioArtifact(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplicationStudioGenerateInput) =>
      generateApplicationStudioArtifact(projectId, input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECTS,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECT(projectId),
      });
      result.artifacts.forEach((artifact) => {
        queryClient.setQueryData(
          QUERY_KEYS.APPLICATION_STUDIO_ARTIFACT(artifact.id),
          artifact,
        );
      });
      queryClient.invalidateQueries({
        queryKey: ['application-studio', 'artifacts'],
      });
    },
  });
}

export function useUpdateApplicationStudioArtifact(artifactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof updateApplicationStudioArtifact>[1]) =>
      updateApplicationStudioArtifact(artifactId, input),
    onSuccess: (artifact) => {
      queryClient.setQueryData(
        QUERY_KEYS.APPLICATION_STUDIO_ARTIFACT(artifact.id),
        artifact,
      );
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_ARTIFACTS(
          artifact.projectId,
          artifact.kind,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_ARTIFACTS(null, artifact.kind),
      });
    },
  });
}

export function useRegenerateApplicationStudioArtifact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateApplicationStudioArtifact,
    onSuccess: (result) => {
      result.artifacts.forEach((artifact) => {
        queryClient.setQueryData(
          QUERY_KEYS.APPLICATION_STUDIO_ARTIFACT(artifact.id),
          artifact,
        );
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECTS,
      });
      queryClient.invalidateQueries({
        queryKey: ['application-studio', 'artifacts'],
      });
    },
  });
}

export function useDeleteApplicationStudioArtifact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteApplicationStudioArtifact,
    onSuccess: (_result, artifactId) => {
      queryClient.removeQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_ARTIFACT(artifactId),
      });
      queryClient.invalidateQueries({
        queryKey: ['application-studio', 'artifacts'],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_STUDIO_PROJECTS,
      });
    },
  });
}

export function usePrepareApplicationStudioApply(projectId: string) {
  return useMutation({
    mutationFn: (input: ApplicationStudioApplyInput) =>
      prepareApplicationStudioApply(projectId, input),
  });
}

export function useSubmitApplicationStudioApply(projectId: string) {
  return useMutation({
    mutationFn: (input: ApplicationStudioSubmitApplyInput) =>
      submitApplicationStudioApply(projectId, input),
  });
}
